<script setup lang="ts">
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-vue-next';
import type { Component } from 'vue';

import { dismiss, useToastState, type ToastType } from '@/composables/useToast.js';

const state = useToastState();
const ICON: Record<ToastType, Component> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};
</script>

<template>
  <div class="toasts" aria-live="polite">
    <TransitionGroup name="toast">
      <div v-for="t in state.items" :key="t.id" class="toast" :class="`toast--${t.type}`" role="status">
        <component :is="ICON[t.type]" :size="18" class="toast__icon" />
        <span class="toast__msg">{{ t.message }}</span>
        <button class="toast__close" type="button" aria-label="Закрыть" @click="dismiss(t.id)">
          <X :size="15" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  top: var(--space-4);
  right: var(--space-4);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-width: min(400px, calc(100vw - var(--space-8)));
}
.toast {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-3);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-left-width: 3px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  font-size: var(--font-size-base);
  color: var(--color-text);
}
.toast--success {
  border-left-color: var(--color-success);
}
.toast--success .toast__icon {
  color: var(--color-success);
}
.toast--error {
  border-left-color: var(--color-danger);
}
.toast--error .toast__icon {
  color: var(--color-danger);
}
.toast--info {
  border-left-color: var(--color-info);
}
.toast--info .toast__icon {
  color: var(--color-info);
}
.toast__icon {
  flex: none;
}
.toast__msg {
  flex: 1;
  line-height: var(--line-height-tight);
}
.toast__close {
  flex: none;
  display: inline-flex;
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 2px;
  border-radius: var(--radius-sm);
}
.toast__close:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
}
.toast-enter-active,
.toast-leave-active {
  transition: all var(--transition-base);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(16px);
}
</style>
